import { useDispatch, useSelector } from "react-redux";
import ProductResults from "~/features/search/components/ProductResults/ProductResults";
import { salesCampaignMounted } from "~/features/search/redux/actions";
import { selectIsInFlight, selectIsInit, selectProductResultAmount } from "~/features/search/redux/searchSelectors";
import { useOnMount } from "~/common/hooks/useOnMount";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { SearchPageTypeProvider } from "~/features/search/context/SearchPageTypeContext";
import { RecentlyViewedProducts } from "~/features/RecentlyViewedProducts/RecentlyViewedProducts";
import { Button, Grid, Stack, styled, Typography } from "~/common/mui";
import { ReduxFilterSection } from "~/features/search/components/Filters/ReduxFilterSection";
import classes from "~/features/search/components/ProductResults/ProductResults.module.less";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { trackSalesPushReportDownload } from "~/app/analytics/ga/ga";
import {
  downloadSalesCampaignCsv,
  downloadSalesCampaignExcel,
} from "~/features/salesCampaign/utils/downloadSalesCampaign";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

export const SalesCampaignPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useOnMount(() => {
    dispatch(salesCampaignMounted());
  });

  const inFlight = useSelector(selectIsInFlight);
  const productAmount = useSelector(selectProductResultAmount);
  let pageTitle = t("page.plp.tab.search");
  if (!inFlight) {
    pageTitle += " (" + productAmount + ")";
  }
  const init = useSelector(selectIsInit);

  return (
    <SearchPageTypeProvider overrideType={SearchPageType.Campaign}>
      <Stack gap={3}>
        <PageHeader>
          <Typography variant="headlineLarge" sx={{ fontWeight: "bold" }} component="h1">
            {pageTitle}
          </Typography>
          <CampaignPageDownloads />
        </PageHeader>

        <Grid container>
          <Grid
            sx={{ display: { phone: "none", "desktop-s": "block" } }}
            size={{
              "desktop-s": 3,
            }}
          >
            <ReduxFilterSection loading={!init} />
          </Grid>

          <Grid
            size={{
              phone: 12,
              "desktop-s": 9,
            }}
          >
            <ProductResults />
          </Grid>
        </Grid>
      </Stack>
      <RecentlyViewedProducts />
    </SearchPageTypeProvider>
  );
};

export const createSalesCampaignPage = () => <SalesCampaignPage />;

const CampaignPageDownloads = () => {
  const { t } = useTranslation();
  const { val: excelLoading, setTrue: startExcelLoading, setFalse: endExcelLoading } = useBooleanState(false);
  const { val: csvLoading, setTrue: startCsvLoading, setFalse: endCsvLoading } = useBooleanState(false);

  return (
    <div className={classNames(classes.downloadContainer, classes.downloadText)}>
      <span className={classes.downloadLabel}>{`${t("page.campaign.download")}:`}</span>
      <Button
        loading={excelLoading}
        className="label-large"
        disabled={excelLoading || csvLoading}
        variant={"text"}
        startIcon={<Icon type={IconType.Download} />}
        onClick={async () => {
          startExcelLoading();
          trackSalesPushReportDownload("xlsx");
          await downloadSalesCampaignExcel();
          endExcelLoading();
        }}
      >
        {t("downloadFile.type.excel")}
      </Button>
      <Button
        loading={csvLoading}
        className="label-large"
        disabled={excelLoading || csvLoading}
        variant={"text"}
        startIcon={<Icon type={IconType.Download} />}
        onClick={async () => {
          startCsvLoading();
          trackSalesPushReportDownload("csv");
          await downloadSalesCampaignCsv();
          endCsvLoading();
        }}
      >
        {t("downloadFile.type.csv")}
      </Button>
    </div>
  );
};

const PageHeader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});
